import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";

// Wire types match internal/httpapi/handlers/ai_refill.go. Kept hand-rolled
// rather than generated because the shape is small and stable; if/when we
// emit OpenAPI we can replace these from the generator.

export type AIRole = "user" | "assistant";

export interface AIContentBlock {
  type: "text" | "tool_use" | "tool_result";
  text?: string;
  tool_use?: {
    id: string;
    name: string;
    input: unknown;
  };
  tool_result?: {
    tool_use_id: string;
    content: string;
    is_error?: boolean;
  };
}

export interface AIMessage {
  role: AIRole;
  content: AIContentBlock[];
}

export interface AITurnRequest {
  message: string;
  history?: AIMessage[];
  model?: string;
}

export interface AITurnUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
}

export interface AITurnResponse {
  llm_call_id: string;
  final: AIMessage;
  history: AIMessage[];
  usage: AITurnUsage;
}

// Per-turn telemetry for the persisted AI feed (one llm_calls row each).
export interface AITurnMeta {
  llm_call_id: string;
  created_at: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  error_code?: string;
}

// The persisted AI conversation for a session, reconstructed server-side from
// llm_calls. `history` is the continuous thread (same shape useAITurn returns),
// so the wizard re-seeds its shared conversation from it and the detail page
// renders it read-only.
export interface SessionAITurns {
  history: AIMessage[];
  turns: AITurnMeta[];
}

// useSessionAITurns loads the persisted AI conversation for a refill session.
// It survives navigation AND full reloads (the backend reads llm_calls), which
// is what lets the wizard's analysis / prioritized-actions persist and the
// detail page show the AI trail. Invalidated by useAITurn after each turn.
export function useSessionAITurns(sessionID: string | undefined) {
  return useQuery({
    queryKey: sessionID
      ? queryKeys.aiTurns.forSession(sessionID)
      : queryKeys.aiTurns.forSession("none"),
    queryFn: ({ signal }) =>
      api.get<SessionAITurns>(
        `/v1/refill-sessions/${sessionID}/ai/turns`,
        undefined,
        signal,
      ),
    enabled: !!sessionID,
  });
}

// useAITurn drives one AI tool-use loop for the given session. On success,
// the refill-session + refill-step queries are invalidated so any
// record_step / revise_step / void_step calls the AI made surface in the
// UI without a manual refetch.
export function useAITurn(sessionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AITurnRequest) =>
      api.post<AITurnResponse>(
        `/v1/refill-sessions/${sessionID}/ai/turn`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.refillSteps.forSession(sessionID),
      });
      qc.invalidateQueries({ queryKey: queryKeys.refillSessions.all });
      qc.invalidateQueries({ queryKey: queryKeys.pendingActions.all });
      // Refresh the persisted thread so the detail page (and any other reader)
      // picks up this turn; the wizard owns its live thread in component state.
      qc.invalidateQueries({
        queryKey: queryKeys.aiTurns.forSession(sessionID),
      });
    },
  });
}

// ─────────── Manual-mode fallback ───────────

export interface MixStep {
  order_rank: number;
  nutrient_id: string;
  nutrient_name: string;
  ml_per_gal: number;
  amount_ml: number;
  gate_rules: { if: string; then: string }[];
  contains_calcium: boolean;
  contains_phosphate: boolean;
  is_biological: boolean;
  is_ph_adjuster: boolean;
  notes: string;
}

export interface SafetyViolation {
  kind: string;
  detail: string;
  step_index: number;
}

export interface StepEvaluation {
  index: number;
  phase: string;
  nutrient_id: string;
  ml_dosed: number;
  predicted_post_ph: number;
  predicted_post_ec: number;
  predicted_post_temp: number;
  observed_post_ph: number;
  observed_post_ec: number;
  observed_post_temp: number;
  delta_ph: number;
  delta_ec: number;
  delta_temp: number;
  voided: boolean;
  confidence: string;
}

export interface Trajectory {
  steps: StepEvaluation[];
  safety_violations: SafetyViolation[];
  predicted_final_ph: number;
  predicted_final_ec: number;
  predicted_final_temp: number;
}

export interface ManualEvaluateResponse {
  plan: MixStep[];
  safety_violations: SafetyViolation[];
  trajectory: Trajectory;
}

// useManualEvaluate fetches the engine-only evaluation for the session.
// Used when the AI is unavailable (offline, budget exceeded, manual toggle).
export function useManualEvaluate(sessionID: string) {
  return useMutation({
    mutationFn: () =>
      api.post<ManualEvaluateResponse>(
        `/v1/refill-sessions/${sessionID}/ai/evaluate-manual`,
        {},
      ),
  });
}
